import { memo } from 'react';

import { useChartLayoutLoading } from '../../../core/components/chart-layout/hooks/useChartLayoutLoading.js';
import { useChartLayoutOrientation } from '../../../core/components/chart-layout/hooks/useChartLayoutOrientation.js';
import { useChartLayoutSize } from '../../../core/components/chart-layout/hooks/useChartLayoutSize.js';
import { getLegendOrientation } from '../../../core/components/chart-layout/utils/orientation.js';
import { ChartLegend } from '../../../core/components/legend/categorical/ChartLegend.js';
import { useLegendSeriesActions } from '../../../core/components/legend/categorical/hooks/useLegendSeriesActions.js';
import { ORIENTATION } from '../../../core/constants.js';
import { useTruncationMode } from '../../context/FormatterContext.js';
import { usePieChartSlicesData } from '../../context/PieChartSlicesData.js';
import { usePieHandlers } from '../../hooks/usePieHandlers.js';
import { InternalSliceData } from '../../types/internal/pie-chart.js';

/**
 * ChartLegend component.
 */
export const PieChartLegend = memo(() => {
  const { slices } = usePieChartSlicesData();
  const { height, width, wrapperHeight } = useChartLayoutSize();
  const { orientation: layoutOrientation } = useChartLayoutOrientation();
  const hideLegendForAspectRatio =
    layoutOrientation === ORIENTATION.Horizontal
      ? width < height
      : wrapperHeight < height;

  const { onLegendItemHover, onLegendItemClicked } = usePieHandlers(slices);

  const legendOrientation = getLegendOrientation(layoutOrientation);
  const truncationMode = useTruncationMode();
  const legendActions = useLegendSeriesActions<InternalSliceData>(
    slices,
    (s) => s.id,
  );
  const isLoading = useChartLayoutLoading();

  const legendItems = slices.map((slice) => ({
    id: slice.id,
    color: slice.color,
    name: slice.category,
  }));

  return (
    !hideLegendForAspectRatio && (
      <ChartLegend
        items={legendItems}
        orientation={legendOrientation}
        onHover={onLegendItemHover}
        onClick={onLegendItemClicked}
        onSeriesAction={legendActions}
        truncationMode={truncationMode}
        interactionsDisabled={isLoading}
      />
    )
  );
});

PieChartLegend['displayName'] = 'PieChartLegend';
